import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dimensions,
  Pressable,
  StyleSheet,
  Text,
  View,
  type StyleProp,
  type TextStyle,
  type ViewStyle,
} from 'react-native';
import { Icon, type IconName } from '../index';
import { webTapFloor } from '../theme';

import type { useTheme } from './theme';
import { useHintsEnabled } from './state/hintsPreference';

// Reusable in-house transient tooltip for a tappable label (first use: the
// logger set-table column headers, D3 2026-05-29). RN has no native tooltip
// and we stay dependency-free. Tap the label → an absolutely-positioned
// bubble appears below it, auto-dismissing after a few seconds (tap again to
// close early). It carries the per-column explanation a terse label can't —
// e.g. in a mixed superset the weight column is barbell load on one row and
// ± extra on another.
//
// Reuse contract (Antenore 2026-05-29): generic, i18n-agnostic (caller passes
// already-translated `label` + `tip`), respects the global Hints toggle
// (Settings → off ⇒ plain label, no affordance), and bounds the text via
// `maxChars` so a long tip can never blow out the bubble / get clipped. The
// bubble is centred on the trigger by default so it stays inside the card
// (`align` overrides for edge columns).
const AUTO_DISMISS_MS = 4500;
const DEFAULT_MAX_CHARS = 180;
const BUBBLE_WIDTH = 260;
const SCREEN_MARGIN = 8;

export function HeaderTooltip({
  label,
  tip,
  labelStyle,
  cellStyle,
  a11yLabel,
  maxChars = DEFAULT_MAX_CHARS,
  labelNumberOfLines,
  alwaysOn = false,
  leadingIcon,
  theme,
}: {
  /** The visible label (kept unit-aware: KG / LB / EXTRA / RIR …). */
  label: string;
  /** The explanation shown in the bubble (already localized by the caller). */
  tip: string;
  labelStyle: StyleProp<TextStyle>;
  /** The column style (e.g. colWeight, width 68) — drives the cell width. */
  cellStyle: StyleProp<ViewStyle>;
  a11yLabel?: string;
  /** Hard cap on the tip length; longer text is truncated with an ellipsis
   *  so the bubble can never grow unbounded or clip. */
  maxChars?: number;
  /** Truncate the trigger label (e.g. a 2-line note whose bubble carries
   *  the full text). Default: no truncation, the header-label use. */
  labelNumberOfLines?: number;
  /** Skip the global Hints gate. For triggers whose bubble is CONTENT, not
   *  a hint — e.g. the truncated exercise note, where the bubble is the
   *  only way to read the full text (Antenore 2026-06-12). */
  alwaysOn?: boolean;
  /** Optional leading glyph rendered before the label (e.g. the per-set cue
   *  🎯 / per-exercise note 💬). Neutral `ink2`, sized to the label so it reads
   *  as part of the line, not the brand — obeys the colour budget. */
  leadingIcon?: IconName;
  theme: ReturnType<typeof useTheme>;
}) {
  const { enabled: hintsEnabled } = useHintsEnabled();
  const [open, setOpen] = useState(false);
  // Bubble's left, relative to the cell, computed on open so the bubble sits
  // centred on the SCREEN (≈ the card) and clamped to the edges — fixes the
  // v2.59 single-row clip and works for any column (Antenore 2026-05-29).
  const [bubbleLeft, setBubbleLeft] = useState(0);
  const cellRef = useRef<View>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    [],
  );
  const styles = useMemo(() => makeStyles(theme), [theme]);

  // The label, optionally preceded by a neutral leading glyph. RN can't nest
  // an <Icon> (svg) inside <Text>, so the icon + text sit in a row; the
  // underline cue (interactive case) stays on the Text alone.
  const renderLabel = (interactive: boolean) => {
    const text = (
      <Text
        style={interactive ? [labelStyle, styles.labelHinted] : labelStyle}
        numberOfLines={labelNumberOfLines}
      >
        {label}
      </Text>
    );
    if (!leadingIcon) return text;
    return (
      <View style={styles.labelRow}>
        <Icon
          name={leadingIcon}
          size={theme.typography.sizes.sm}
          color={theme.palette.ink2}
        />
        {text}
      </View>
    );
  };

  // All hooks above are unconditional — the early return below is safe.
  // Hints disabled (Settings) → plain, non-interactive label. The cell
  // wrapper stays so the column geometry is identical either way.
  if (!hintsEnabled && !alwaysOn) {
    return <View style={[styles.cell, cellStyle]}>{renderLabel(false)}</View>;
  }

  const toggle = () => {
    if (timer.current) clearTimeout(timer.current);
    if (open) {
      setOpen(false);
      return;
    }
    // Measure the cell, centre the bubble on the screen (clamped), then show
    // it already positioned. The timer lives outside setOpen so a replayed
    // updater can't orphan it.
    cellRef.current?.measureInWindow((x) => {
      const screenW = Dimensions.get('window').width;
      // Screen-centred, clamped to the margins.
      const target = Math.min(
        Math.max(screenW / 2 - BUBBLE_WIDTH / 2, SCREEN_MARGIN),
        screenW - BUBBLE_WIDTH - SCREEN_MARGIN,
      );
      // `target` is a screen-x; the bubble is positioned within the cell, so
      // rebase to the cell's screen-x (x).
      setBubbleLeft(target - x);
      setOpen(true);
      timer.current = setTimeout(() => setOpen(false), AUTO_DISMISS_MS);
    });
  };

  const shownTip =
    tip.length > maxChars ? `${tip.slice(0, maxChars - 1).trimEnd()}…` : tip;

  return (
    <View ref={cellRef} style={[styles.cell, cellStyle]}>
      <Pressable
        onPress={toggle}
        accessibilityRole="button"
        accessibilityLabel={a11yLabel ?? label}
        accessibilityHint={shownTip}
        hitSlop={8}
        style={styles.hitBox}
      >
        {renderLabel(true)}
      </Pressable>
      {open ? (
        <View
          style={[styles.bubble, { left: bubbleLeft }]}
          accessibilityRole="text"
          pointerEvents="none"
        >
          <Text style={styles.bubbleText}>{shownTip}</Text>
        </View>
      ) : null}
    </View>
  );
}

function makeStyles(theme: ReturnType<typeof useTheme>) {
  const { palette: p, spacing: s, radius: r, typography: t } = theme;
  return StyleSheet.create({
    cell: {
      // Anchor for the absolute bubble; keep the label centered like the
      // plain header Text it replaces (colWeight had textAlign:'center').
      // Applied BEFORE cellStyle so a caller can override the alignment (e.g.
      // the timeline FocusPanel field label, which is left-aligned).
      alignItems: 'center',
      justifyContent: 'center',
      // Matches `hitBox` below so the NON-interactive branch (Hints off —
      // no Pressable at all, see the early return above) keeps the same
      // geometry the comment there promises.
      minHeight: webTapFloor,
    },
    // The interactive Pressable relies on hitSlop=8 alone to reach the 44pt
    // floor, which react-native-web's Pressable doesn't honour (DS-51) —
    // same idiom as Button/Chip/IconButton (`webTapFloor` on `minHeight`,
    // undefined/no-op on native). Applied to the Pressable itself, not only
    // `cell`: a taller ANCESTOR doesn't grow the actual clickable node —
    // RNW hit-tests the Pressable's own box, so centering a small Pressable
    // inside a taller `cell` left the real click target unchanged (caught by
    // re-testing after the first attempt landed on `cell` alone). Not
    // padding+negative-margin either (tried and reverted: it clears 44 too,
    // but the invisible overshoot silently steals taps meant for whatever
    // sits in the 4px gap below/beside it — confirmed empirically on the
    // FocusPanelInputs weight TextInput, DS-51 investigation notes). A real
    // `minHeight` RESERVES the extra space in the flow, so it can never
    // overlap a neighbour — it only ever pushes one further away (harmless,
    // and already the norm for every other DS-48/50/51 fix).
    hitBox: {
      minHeight: webTapFloor,
      // Width too, not just height: two callers (FocusPanelInputs' weight
      // field, FocusPanelDetails' note cards) already guarantee ≥44 width
      // via a stretching/flex parent, so this is a no-op there — but
      // TimelineList's e1RM legend cell shrink-wraps to its own short label
      // ("e1RM" at `xs`, plus the `leadingIcon="info"` that replaced the glyph
      // it used to carry), with no such parent, and was left under the floor
      // without it.
      minWidth: webTapFloor,
      justifyContent: 'center',
      alignItems: 'center',
    },
    // Icon + label on one line; flex-start so the glyph aligns to the first
    // line of a wrapping (2-line) note rather than centering on the block.
    labelRow: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      gap: s.s1,
    },
    // A dotted underline cue that the label is tappable, without adding a
    // glyph or width (accessibility: not colour-only).
    labelHinted: {
      textDecorationLine: 'underline',
      textDecorationStyle: 'dotted',
    },
    bubble: {
      position: 'absolute',
      top: '100%',
      marginTop: s.s1,
      width: BUBBLE_WIDTH,
      backgroundColor: p.ink,
      borderRadius: r.r2,
      paddingHorizontal: s.s3,
      paddingVertical: s.s2,
      // Float above the set rows beneath the header. `left` is set inline
      // from the measured screen-centre (see toggle).
      zIndex: 50,
      elevation: 8,
    },
    bubbleText: {
      color: p.bg,
      fontSize: t.sizes.xs,
      lineHeight: t.sizes.xs * 1.4,
      fontWeight: '500',
    },
  });
}
